import { useEffect, useState } from 'react'
import { useRouter } from 'next/router'
import { Field, useFormState } from 'react-final-form'
import { AUTH_TYPE } from '@mc/constants'
import { Button, CheckboxField } from '@mc/design-system'
import {
  EmailCaptureError,
  EmailCaptureForm,
  EmailCaptureInput,
  EmailWarning,
  setFireEmailCaptureCookie,
  useEmailCapture,
} from '@mc/email-capture'
import { useTranslation } from '@mc/i18n'
import { EmailCaptureLegal } from '@mc/legal'
import { CartEvents } from '@mc/track-event'
import { fireFooterEmailCapturedEvent } from './events/trackFooterEmailCaptured'
import { GLOBAL_FOOTER_EMAIL_I18N_NAMESPACE } from './i18n'

const SubmitButton = ({
  loading,
  label,
}: {
  loading: boolean
  label: string
}) => {
  const { pristine, hasValidationErrors, values } = useFormState({
    subscription: { pristine: true, hasValidationErrors: true, values: true },
  })

  return (
    <Button
      type='submit'
      loading={loading}
      disabled={pristine || hasValidationErrors || !values?.consent}
    >
      {label}
    </Button>
  )
}

export const FooterEmailCaptureSection = () => {
  const { t } = useTranslation(GLOBAL_FOOTER_EMAIL_I18N_NAMESPACE)
  const { t: tShared } = useTranslation('@mc/next')
  const [state, setState] = useState<'form' | 'success'>('form')
  const {
    isLoading,
    isError,
    isSuccess,
    isEmailRegistered,
    onCheckEmail,
    email,
  } = useEmailCapture()

  useEffect(() => {
    if (!isLoading && isSuccess && email && !isEmailRegistered) {
      setFireEmailCaptureCookie({
        authType: AUTH_TYPE.EMAIL,
        captureMethod: CartEvents.EMAIL_GATHER.ADDED_IN_EMAIL_STEP,
      })
      fireFooterEmailCapturedEvent(email)
      setState('success')
    }
  }, [isLoading, isSuccess, email, isEmailRegistered])

  const { asPath } = useRouter()

  if (state === 'success') {
    return (
      <div role='status'>
        <h3 className='mc-text-h5 mc-mb-2'>{t('success.headline')}</h3>
        <p className='mc-text-small mc-opacity--muted'>
          {t('success.message')}
        </p>
      </div>
    )
  }

  return (
    <div>
      <h3 className='mc-text-h5 mc-mb-2'>{t('headline')}</h3>
      <p className='mc-text-small mc-opacity--muted mc-mb-6'>
        {t('subheadline')}
      </p>

      <EmailCaptureForm onSubmit={onCheckEmail}>
        <div className='d-flex align-items-center mc-gap-3 mc-mb-3'>
          <div className='flex-grow-1'>
            <EmailCaptureInput
              id='footerEmail'
              placeholder={t('emailPlaceholder')}
              showLabel={false}
            />
          </div>
          <SubmitButton loading={isLoading} label={t('cta')} />
        </div>

        {!isLoading && isError && <EmailCaptureError className='mc-mb-2' />}

        {isEmailRegistered && (
          <EmailWarning
            variant='already-registered'
            returnUrl={asPath}
            onClick={() => {}}
          />
        )}

        <Field
          id='consent'
          className='mc-mb-2 align-items-start mc-text-x-small'
          component={CheckboxField}
          name='consent'
          label={
            <label className='mc-opacity--muted'>
              {tShared('emailCaptureBanner.form.agree')}
            </label>
          }
        />

        <EmailCaptureLegal
          kind='submit'
          className='mc-text-x-small mc-opacity--muted'
          ctaText={t('cta')}
        />
      </EmailCaptureForm>
    </div>
  )
}
